import {createSelector} from '@reduxjs/toolkit';
import getCorrectErrorMessage from 'classnames';

import {STEPS} from 'desktop/pages/checkout/constants/checkout';
import {ApplicationStoreType} from 'desktop/store';

import {CustomerAddressesType, CustomerAddressType} from 'desktop/types/customerAddressesType';

import {MODULE_KEY_CHECKOUT} from '../constants';
import {getStepStatus} from '../currentStep/currentStep';
import {getShippingAddressStateName} from '../shippingAddress/shippingAddress';
import {
    getShippingMethodsCount,
    getShippingMethodsError,
    getShippingMethodsStatus,
} from '../shippingMethods/shippingMethods';

/**
 * Get delivery step
 * @param {ApplicationStoreType} store - current store
 */
export const getDeliveryStep = (store: ApplicationStoreType) =>
    store[MODULE_KEY_CHECKOUT].deliveryStep;

/**
 * Get list of customer addresses
 * @param {ApplicationStoreType} store - current store
 * @return {CustomerAddressesType} list of addresses
 */
export const getCustomerAddresses = (store: ApplicationStoreType): CustomerAddressesType =>
    store[MODULE_KEY_CHECKOUT].deliveryStep.customerAddresses;

/**
 * Get list of addresses
 * @param {ApplicationStoreType} store - current store
 * @return {Array<CustomerAddressType>} addresses
 */
export const getAddresses = (store: ApplicationStoreType): Array<CustomerAddressType> =>
    store[MODULE_KEY_CHECKOUT].deliveryStep.customerAddresses.addresses;

/**
 * Get is show california preposition popup
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} is show popup
 */
export const getIsShowCaliforniaPrepositionPopUp = (store: ApplicationStoreType): boolean =>
    store[MODULE_KEY_CHECKOUT].showCaliforniaPrepositionPopup;

/**
 * Get sameness addresses
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} is sameness addresses
 */
export const getAddressesSameness = (store: ApplicationStoreType): boolean =>
    store[MODULE_KEY_CHECKOUT].deliveryStep.billingAndShippingIsSame;

/**
 * Returns billing region
 *
 * @param {ApplicationStoreType} store - current store
 * @return {string} shipping region
 */
const getShippingRegion = (store: ApplicationStoreType): string =>
    store[MODULE_KEY_CHECKOUT].shippingRegion;

/**
 * Get error message
 * @param {ApplicationStoreType} store - current store
 * @return {string} error message
 */
export const getSaveAddressErrorMessage = (store: ApplicationStoreType): string =>
    store[MODULE_KEY_CHECKOUT].deliveryStep.errorMessage;

/**
 * Is delivery step loaded
 * @param {ApplicationStoreType} store - current store
 * @returns {boolean} true if delivery step is loaded
 */
export const isDeliveryStepLoaded = (store: ApplicationStoreType): boolean =>
    Boolean(getStepStatus(store, STEPS.DELIVERY_STEP).loaded);

/**
 * Get active address
 * @param {ApplicationStoreType} store - current store
 * @param {'shipping' | 'billing'} type - current type
 * @returns {CustomerAddressType | undefined} active address
 */
export const getActiveAddress = createSelector(
    [getCustomerAddresses, (store: ApplicationStoreType, type: 'shipping' | 'billing') => type],
    ({addresses, billing_address_id: billingID, shipping_address_id: shippingID}, type) => {
        const active = type === 'shipping' ? shippingID : billingID;
        return addresses.find(address => Number(address.addressId) === active);
    },
);

/**
 * Get is show address
 */
export const getIsShowAddress = createSelector(
    getAddressesSameness,
    getCustomerAddresses,
    (billingAndShippingIsSame, {addresses}) => !billingAndShippingIsSame && addresses.length > 1,
);

/**
 * Get is show address form
 */
export const getIsShowAddressForm = createSelector(
    getAddressesSameness,
    getCustomerAddresses,
    (billingAndShippingIsSame, {addresses}) => !billingAndShippingIsSame && addresses.length === 1,
);

/**
 * Determines whether shipping address state is California or other
 *
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} indicates that user chose california as shipping address state
 */
export const isCaliforniaStateSelected = createSelector(
    getShippingAddressStateName,
    getShippingRegion,
    (shippingAddressStateName: string, shippingRegion: string): boolean =>
        (shippingRegion || shippingAddressStateName) === 'California',
);

/**
 * Get active address id
 * @param {ApplicationStoreType} store - current store
 * @param {'shipping' | 'billing'} type - current type
 * @return {number | null} active address id
 */
export const getAddressesActiveId = createSelector(
    [getCustomerAddresses, (store: ApplicationStoreType, type: 'shipping' | 'billing') => type],
    ({billing_address_id: billingId, shipping_address_id: shippingId}, type): Maybe<number> =>
        type === 'shipping' ? shippingId : billingId,
);

/**
 * Get disable address id
 *
 * @param {ApplicationStoreType} store - current store
 * @param {'shipping' | 'billing'} type - current type
 * @return {number | null} disable address id
 */
export const getAddressesDisableId = createSelector(
    [getCustomerAddresses, (store: ApplicationStoreType, type: 'shipping' | 'billing') => type],
    ({billing_address_id: billingId, shipping_address_id: shippingId}, type): Maybe<number> => {
        if (type === 'billing') return shippingId;

        return shippingId === billingId ? null : billingId;
    },
);

/**
 * True if there is no shipping methods for checkout
 */
export const isThereAnyShipping = createSelector(
    getShippingMethodsStatus,
    getShippingMethodsCount,
    (methodsStatus, methodsCount): boolean => methodsStatus === 'FULFILLED' && !methodsCount,
);

/**
 * Get continue button is disabled
 */
export const getContinueButtonIsDisabled = createSelector(
    isThereAnyShipping,
    getShippingMethodsError,
    (isShipping, shippingError): boolean => isShipping || Boolean(shippingError),
);

/**
 * Return errors for delivery step
 */
export const getDeliveryErrors = createSelector(
    isThereAnyShipping,
    getShippingMethodsError,
    (isShipping, shippingError): string => {
        return getCorrectErrorMessage({
            'Sorry we are not shipping to this region at this time': isShipping,
            // eslint-disable-next-line perfectionist/sort-objects
            [shippingError]: Boolean(shippingError),
        });
    },
);

/**
 *  return region id
 */
export const getShippingAddressRegionId = (store: ApplicationStoreType): string =>
    store[MODULE_KEY_CHECKOUT].deliveryStep.shippingAddress.regionID.value;
